import { useEffect, useMemo, useRef, useState } from "react";
import { CheckCircle2, Loader2, Lock, Sparkles, TriangleAlert } from "lucide-react";
import {
  errorMessage,
  previewTemplate,
  type AiVariable,
  type PreviewResponse,
} from "@/api/email";
import { Button } from "@/components/ui/button";
import { ContactPicker, type PickedContact } from "@/components/email/ContactPicker";
import { TemplateText, tokenClass } from "@/components/email/TemplateText";
import { RENDER_ERROR_LABEL, aiKeysIn } from "@/lib/email";
import { cn } from "@/lib/utils";

/**
 * The editor's right side: the email exactly as one real contact would get
 * it. Contact and company variables resolve as you type (debounced); AI
 * variables only on demand — never on a keystroke.
 */
export function PreviewPanel({
  slug,
  campaignId,
  stepId,
  position,
  subject,
  body,
  aiVariables,
  saved,
}: {
  slug: string;
  campaignId: number;
  /** The saved step, when editing one. */
  stepId: number | null;
  position: number;
  subject: string;
  body: string;
  aiVariables: AiVariable[];
  /** The editor shows the saved step's content (no unsaved change). */
  saved: boolean;
}) {
  const [contact, setContact] = useState<PickedContact | null>(null);
  const [preview, setPreview] = useState<PreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Values generated in this editor, per contact, kept while the prompts are unchanged.
  const [generated, setGenerated] = useState<{ contactId: number; prompts: string; values: Record<string, string> } | null>(null);
  const requestId = useRef(0);

  const prompts = useMemo(() => JSON.stringify(aiVariables.map((v) => [v.key, v.prompt_template, v.model])), [aiVariables]);
  const echo = generated && contact && generated.contactId === contact.id && generated.prompts === prompts ? generated.values : {};
  const usesAi = aiKeysIn(subject, body).length > 0;

  function request(generateAi: boolean) {
    if (!contact) return null;
    return {
      contact_id: contact.id,
      step_id: stepId,
      subject_template: subject,
      body_template: body,
      ai_variables: aiVariables.map((v) => ({ key: v.key, prompt_template: v.prompt_template, model: v.model })),
      generate_ai: generateAi,
      ai_values: generateAi ? {} : echo,
      position,
    };
  }

  // Deterministic preview: debounced, cancelled by newer keystrokes.
  useEffect(() => {
    const body_ = request(false);
    if (!body_) return;
    const id = ++requestId.current;
    setLoading(true);
    const timer = window.setTimeout(() => {
      previewTemplate(slug, body_)
        .then((res) => {
          if (id !== requestId.current) return;
          setPreview(res);
          setError(null);
        })
        .catch((err) => id === requestId.current && setError(errorMessage(err, "The preview failed.")))
        .finally(() => id === requestId.current && setLoading(false));
    }, 350);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- request() reads these
  }, [contact?.id, subject, body, prompts, stepId, position, JSON.stringify(echo)]);

  async function generate() {
    const body_ = request(true);
    if (!body_ || !contact) return;
    const id = ++requestId.current;
    setGenerating(true);
    try {
      const res = await previewTemplate(slug, body_);
      if (id !== requestId.current) return;
      setPreview(res);
      setGenerated({ contactId: contact.id, prompts, values: res.ai_values });
      setError(null);
    } catch (err) {
      setError(errorMessage(err, "The AI preview failed."));
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  }

  const errors = preview?.errors ?? [];
  const aiErrors = Object.entries(preview?.ai_errors ?? {});
  // Not generated yet is not a problem: the sync generates them per contact.
  const pendingAi = errors.filter((e) => e.kind === "ai_not_generated");
  const blocking = errors.filter((e) => e.kind !== "ai_not_generated");
  const problems = blocking.length + aiErrors.length;
  const frozen = saved && contact?.inCampaign && stepId !== null;

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex items-center justify-between gap-3">
        <p className="type-zonehead">Preview</p>
        {(usesAi || aiVariables.length > 0) && (
          <Button
            size="sm"
            variant="outline"
            disabled={!contact || generating}
            onClick={() => void generate()}
            className="border-fuchsia-200 text-fuchsia-700 hover:bg-fuchsia-50 hover:text-fuchsia-800"
          >
            {generating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
            Generate AI preview
          </Button>
        )}
      </div>

      <ContactPicker slug={slug} campaignId={campaignId} value={contact} onChange={setContact} />

      <article className="overflow-hidden rounded-xl bg-white shadow-card-rest ring-1 ring-slate-900/[0.04]">
        <header className="space-y-1.5 border-b border-slate-100 px-4 py-3 text-sm">
          <p className="flex gap-2">
            <span className="w-14 shrink-0 text-xs text-slate-400">To</span>
            <span className="truncate text-slate-700">
              {preview ? (
                <>
                  {preview.contact.full_name}{" "}
                  <span className="text-slate-400">
                    {preview.contact.hidden_details.includes("email") ? (
                      <span className="inline-flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        email hidden
                      </span>
                    ) : (
                      `<${preview.contact.email ?? "no email"}>`
                    )}
                  </span>
                </>
              ) : (
                "—"
              )}
            </span>
          </p>
          <p className="flex gap-2">
            <span className="w-14 shrink-0 text-xs text-slate-400">Subject</span>
            <span className="min-w-0 flex-1 font-medium text-slate-900">
              {preview ? (
                preview.subject ? (
                  <TemplateText template={preview.subject} unresolved />
                ) : position > 1 ? (
                  <span className="font-normal italic text-slate-400">Re: — replies in the same thread</span>
                ) : (
                  <span className="font-normal italic text-rose-500">No subject</span>
                )
              ) : (
                "—"
              )}
            </span>
          </p>
        </header>
        <div className={cn("relative min-h-[180px] px-4 py-4", loading && "opacity-60 transition-opacity")}>
          {preview ? (
            preview.body ? (
              <p className="whitespace-pre-wrap text-[14px] leading-relaxed text-slate-700">
                <TemplateText template={preview.body} unresolved />
              </p>
            ) : (
              <p className="text-sm italic text-slate-400">The body is empty.</p>
            )
          ) : (
            <p className="text-sm text-slate-400">{contact ? "Rendering…" : "Choose a contact to preview."}</p>
          )}
          {(loading || generating) && (
            <Loader2 className="absolute right-3 top-3 h-4 w-4 animate-spin text-slate-300" />
          )}
        </div>
      </article>

      {error && <p className="text-sm text-rose-600">{error}</p>}

      {preview && !preview.sendable && problems === 0 && pendingAi.length > 0 && (
        <div className="flex items-start gap-2 rounded-xl bg-fuchsia-50 px-3.5 py-2.5 text-sm text-fuchsia-900">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-fuchsia-500" />
          <div className="min-w-0">
            <p className="font-medium">
              {pendingAi.length === 1 ? "1 AI passage" : `${pendingAi.length} AI passages`} not generated yet
            </p>
            <p className="text-xs text-fuchsia-800/80">
              Use “Generate AI preview” to read the full email. At hand-over, Prism generates them for each contact.
            </p>
          </div>
        </div>
      )}

      {preview && (problems > 0 || preview.sendable) && (
        <div
          className={cn(
            "flex items-start gap-2 rounded-xl px-3.5 py-2.5 text-sm",
            preview.sendable ? "bg-emerald-50 text-emerald-800" : "bg-rose-50 text-rose-800",
          )}
        >
          {preview.sendable ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> : <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />}
          <div className="min-w-0 space-y-1">
            <p className="font-medium">
              {preview.sendable ? "Ready to send for this contact" : `${problems} ${problems === 1 ? "problem" : "problems"} before it can be sent`}
            </p>
            {!preview.sendable && (
              <ul className="flex flex-wrap gap-1.5">
                {blocking.map((e) => (
                  <li key={`${e.kind}-${e.token}`} className="rounded-md bg-white/70 px-1.5 py-0.5 text-xs" title={e.message}>
                    {RENDER_ERROR_LABEL[e.kind] ?? e.kind}
                    {e.token && <span className="ml-1 font-mono text-[11px] text-rose-600">{e.token}</span>}
                  </li>
                ))}
                {aiErrors.map(([key, message]) => (
                  <li key={key} className="rounded-md bg-white/70 px-1.5 py-0.5 text-xs" title={message}>
                    AI generation failed <span className="ml-1 font-mono text-[11px] text-rose-600">ai.{key}</span>
                  </li>
                ))}
              </ul>
            )}
            {!preview.sendable && blocking.some((e) => e.kind === "missing_variable") && (
              <p className="mt-1.5 text-[11px] leading-snug text-slate-600">
                This contact has no value for it. Give the variable a fallback —{" "}
                <span className="font-mono">
                  {`{{${blocking.find((e) => e.kind === "missing_variable")?.token ?? "token"} | "…"}}`}
                </span>{" "}
                — and contacts without one get that text instead of being held back.
              </p>
            )}
          </div>
        </div>
      )}

      {preview && preview.variables.length > 0 && (
        <section className="min-h-0">
          <p className="type-zonehead mb-2">Resolved values</p>
          <dl className="divide-y divide-slate-100 overflow-hidden rounded-xl bg-white ring-1 ring-slate-100">
            {preview.variables.map((v) => (
              <div key={v.token} className="flex items-start gap-3 px-3 py-2">
                <dt className={cn("shrink-0 rounded px-1 font-mono text-[11px] ring-1 ring-inset", tokenClass(v.token))}>{v.token}</dt>
                <dd
                  className={cn(
                    "min-w-0 flex-1 break-words text-xs",
                    v.value || v.fallback != null ? "text-slate-700" : "italic text-rose-500",
                  )}
                >
                  {v.value ? (
                    v.source === "ai" ? `“${v.value}”` : v.value
                  ) : v.fallback != null ? (
                    <>
                      {v.fallback || <span className="italic text-slate-400">(empty)</span>}
                      <span className="ml-1.5 rounded bg-amber-50 px-1 text-[10px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200">
                        fallback · no value
                      </span>
                    </>
                  ) : v.source === "ai" ? (
                    "not generated"
                  ) : (
                    "no value"
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {aiVariables.length > 0 && (
            <p className="mt-2 text-[11px] leading-snug text-slate-400">
              {frozen
                ? "This contact is in the campaign: an AI value generated here is kept and is exactly what they will receive."
                : "AI values are generated for each contact when Prism hands them to Lemlist, and kept from then on."}
            </p>
          )}
        </section>
      )}
    </div>
  );
}
